import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { SimulationTypeEnum } from 'routes/HumanResourcesModule/Simulation/helpers/constants';

import {
    HrSimulation,
    HrSimulationCreatePayload,
    HrSimulationDuplicatePayload,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import MultiStepModal from 'ui/Modals/MultiStepModal';
import HRCreationLoader from 'routes/HumanResourcesModule/components/HRCreationLoader';
import ObsoleteHypotheses from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/components/Duplication/ObsoleteHypotheses/ObsoleteHypotheses';
import { resetDuplicableHypothesesQuery } from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/components/Duplication/DuplicableHypotheses/helpers/hooks/duplicableHypotheses';
import DuplicableHypotheses from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/components/Duplication/DuplicableHypotheses/DuplicableHypotheses';
import { resetObsoleteHypothesesQuery } from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/components/Duplication/ObsoleteHypotheses/helpers/hooks/obsoleteHypotheses';
import SimulationForm from './components/SimulationForm';
import { getRecurrentStartAndEndDates } from './helpers/dataHelpers';

import { filterSteps } from './helpers/modalHelpers';
import { useDuplicationFormState } from './components/Duplication/DuplicableHypotheses/helpers/hooks/useDuplicationForm';
import { useNavigate } from 'react-router-dom';
import { useSimulationFormState } from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/components/hooks/useSimulationFormState';
import {
    checkIsEtablissementsFilterActiveConsistencyWithSelectedEtablissements,
    checkIsPayBudgetsFilterActiveConsistencyWithSelectedPayBudgets,
} from 'routes/HumanResourcesModule/helpers/dataHelpers';

type Props = {
    show: boolean;
    onClose: () => void;
    hrSimulation?: HrSimulation;
    isLoading: boolean;
    onSave: (
        payload: HrSimulationCreatePayload | HrSimulationDuplicatePayload,
    ) => void;
    isError: boolean;
    mainTitle: string;
    creationSubtitle?: string;
    creationDescription?: string;
    actionTitle: string;
    shouldRedirect?: boolean;
};

export default function SimulationModal({
    show,
    onClose,
    hrSimulation,
    onSave,
    isLoading,
    isError,
    mainTitle,
    creationSubtitle,
    creationDescription,
    actionTitle,
    shouldRedirect,
}: Props): React.ReactElement {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const isDuplication = hrSimulation !== undefined;

    const [simulationFormState, setSimulationFormState] =
        useSimulationFormState({
            title: hrSimulation
                ? t('humanResources.simulation.duplicateSimulation.copyTitle', {
                      title: hrSimulation.title,
                  })
                : '',
            isPayBudgetFilterActive:
                hrSimulation?.isPayBudgetFilterActive || false,
            selectedPayBudgetSoftwareIds:
                hrSimulation?.payBudgets.map((et) => et.softwareId) || [],
            isEtablissementsFilterActive:
                hrSimulation?.isEtablissementsFilterActive || false,
            selectedOrgChartHrsSoftwareIds:
                hrSimulation?.orgChartHrs.map((och) => och.softwareId) || [],
            selectedEtablissementSoftwareIds:
                hrSimulation?.etablissements.map((et) => et.softwareId) || [],
            selectedMonth: '',
            selectedType: hrSimulation?.type ?? SimulationTypeEnum.PROJECTION,
            isFormValid: false,
        });
    const [duplicationFormState, setDuplicationFormState] =
        useDuplicationFormState({
            excludedAgentDepartureHypotheses: [],
            excludedAgentArrivalHypotheses: [],
            excludedMantyStatusHypotheses: [],
            excludedPayComponentHypotheses: [],
            excludedAgentPayComponentHypotheses: [],
            excludedGradeAdvancementHypotheses: [],
        });
    const progressSteps = [
        t(`humanResources.simulation.simulationCreation.dataLoad`),
        t(`humanResources.simulation.simulationCreation.creation`),
        t(`humanResources.simulation.simulationCreation.generateEchelons`),
    ];
    const [isObsoleteHypothesesLoading, setIsObsoleteHypothesesLoading] =
        useState(false);

    const [isDuplicableHypothesesLoading, setIsDuplicableHypothesesLoading] =
        useState(false);

    const { recurrentInitialisationStartDate, recurrentInitialisationEndDate } =
        getRecurrentStartAndEndDates(simulationFormState.selectedMonth);

    const onClickSave = () => {
        onSave({
            title: simulationFormState.title,
            isPayBudgetFilterActive:
                checkIsPayBudgetsFilterActiveConsistencyWithSelectedPayBudgets(
                    simulationFormState.isPayBudgetFilterActive,
                    simulationFormState.selectedPayBudgetSoftwareIds,
                ),
            payBudgetSoftwareIds: simulationFormState.isPayBudgetFilterActive
                ? simulationFormState.selectedPayBudgetSoftwareIds
                : [],
            isEtablissementsFilterActive:
                checkIsEtablissementsFilterActiveConsistencyWithSelectedEtablissements(
                    simulationFormState.isEtablissementsFilterActive,
                    simulationFormState.selectedEtablissementSoftwareIds,
                ),
            orgChartHrsSoftwareIds:
                simulationFormState.selectedOrgChartHrsSoftwareIds,
            etablissementSoftwareIds:
                simulationFormState.isEtablissementsFilterActive
                    ? simulationFormState.selectedEtablissementSoftwareIds
                    : [],
            recurrentInitialisationStartDate,
            recurrentInitialisationEndDate,
            type: simulationFormState.selectedType,
            excludedAgentDepartureHypotheses:
                duplicationFormState.excludedAgentDepartureHypotheses,
            excludedAgentArrivalHypotheses:
                duplicationFormState.excludedAgentArrivalHypotheses,
            excludedMantyStatusHypotheses:
                duplicationFormState.excludedMantyStatusHypotheses,
            excludedPayComponentHypotheses:
                duplicationFormState.excludedPayComponentHypotheses,
            excludedAgentPayComponentHypotheses:
                duplicationFormState.excludedAgentPayComponentHypotheses,
            excludedGradeAdvancementHypotheses:
                duplicationFormState.excludedGradeAdvancementHypotheses,
        });
    };

    const redirectHome = () => {
        if (shouldRedirect) {
            navigate(`/human_resources/simulation_home`);
        }
    };
    const handleOrgChartHrsChange = useCallback(() => {
        if (hrSimulation) {
            resetObsoleteHypothesesQuery(queryClient, hrSimulation.id);
            resetDuplicableHypothesesQuery(queryClient, hrSimulation.id);
        }
    }, [hrSimulation, queryClient]);

    const firstStep = useMemo(
        () => (
            <SimulationForm
                formState={simulationFormState}
                setFormState={setSimulationFormState}
                isLoading={isLoading}
                title={mainTitle}
                subtitle={creationSubtitle}
                description={creationDescription}
                onOrgChartHrsChange={handleOrgChartHrsChange}
            />
        ),
        [
            JSON.stringify(simulationFormState),
            setSimulationFormState,
            isLoading,
            mainTitle,
            creationSubtitle,
            creationDescription,
            handleOrgChartHrsChange,
        ],
    );

    const secondStep = useMemo(
        () =>
            hrSimulation ? (
                <ObsoleteHypotheses
                    hrSimulation={hrSimulation}
                    selectedOrgChartHrsSoftwareIds={
                        simulationFormState.selectedOrgChartHrsSoftwareIds
                    }
                    selectedEtablissementSoftwareIds={
                        simulationFormState.isEtablissementsFilterActive
                            ? simulationFormState.selectedEtablissementSoftwareIds
                            : []
                    }
                    isEtablissementsFilterActive={checkIsEtablissementsFilterActiveConsistencyWithSelectedEtablissements(
                        simulationFormState.isEtablissementsFilterActive,
                        simulationFormState.selectedEtablissementSoftwareIds,
                    )}
                    isPayBudgetFilterActive={checkIsPayBudgetsFilterActiveConsistencyWithSelectedPayBudgets(
                        simulationFormState.isPayBudgetFilterActive,
                        simulationFormState.selectedPayBudgetSoftwareIds,
                    )}
                    selectedPayBudgetSoftwareIds={
                        simulationFormState.isPayBudgetFilterActive
                            ? simulationFormState.selectedPayBudgetSoftwareIds
                            : []
                    }
                    setIsObsoleteHypothesesLoading={
                        setIsObsoleteHypothesesLoading
                    }
                    simulationType={simulationFormState.selectedType}
                    recurrentInitialisationStartDate={
                        recurrentInitialisationStartDate
                    }
                />
            ) : null,
        [
            JSON.stringify(hrSimulation),
            simulationFormState.selectedOrgChartHrsSoftwareIds,
            simulationFormState.isEtablissementsFilterActive,
            simulationFormState.selectedEtablissementSoftwareIds,
            simulationFormState.isPayBudgetFilterActive,
            simulationFormState.selectedPayBudgetSoftwareIds,
            simulationFormState.selectedType,
            recurrentInitialisationStartDate,
        ],
    );

    const thirdStep = useMemo(
        () =>
            hrSimulation ? (
                <DuplicableHypotheses
                    hrSimulation={hrSimulation}
                    selectedOrgChartHrsSoftwareIds={
                        simulationFormState.selectedOrgChartHrsSoftwareIds
                    }
                    isEtablissementsFilterActive={checkIsEtablissementsFilterActiveConsistencyWithSelectedEtablissements(
                        simulationFormState.isEtablissementsFilterActive,
                        simulationFormState.selectedEtablissementSoftwareIds,
                    )}
                    selectedEtablissementSoftwareIds={
                        simulationFormState.isEtablissementsFilterActive
                            ? simulationFormState.selectedEtablissementSoftwareIds
                            : []
                    }
                    isPayBudgetFilterActive={checkIsPayBudgetsFilterActiveConsistencyWithSelectedPayBudgets(
                        simulationFormState.isPayBudgetFilterActive,
                        simulationFormState.selectedPayBudgetSoftwareIds,
                    )}
                    selectedPayBudgetSoftwareIds={
                        simulationFormState.isPayBudgetFilterActive
                            ? simulationFormState.selectedPayBudgetSoftwareIds
                            : []
                    }
                    setIsDuplicableHypothesesLoading={
                        setIsDuplicableHypothesesLoading
                    }
                    formState={duplicationFormState}
                    setFormState={setDuplicationFormState}
                    simulationType={simulationFormState.selectedType}
                    recurrentInitialisationStartDate={
                        recurrentInitialisationStartDate
                    }
                />
            ) : null,
        [
            hrSimulation,
            simulationFormState.isEtablissementsFilterActive,
            simulationFormState.selectedEtablissementSoftwareIds,
            simulationFormState.selectedOrgChartHrsSoftwareIds,
            simulationFormState.isPayBudgetFilterActive,
            simulationFormState.selectedPayBudgetSoftwareIds,
            simulationFormState.selectedType,
            duplicationFormState,
            recurrentInitialisationStartDate,
        ],
    );

    const fourthStep = useMemo(
        () => (
            <HRCreationLoader
                isLoading={isLoading}
                isError={isError}
                headerTitle={mainTitle}
                progressSteps={progressSteps}
            />
        ),
        [isLoading, isError, mainTitle],
    );

    const steps = [
        {
            confirmAction: {
                enabled: simulationFormState.isFormValid,
                label: isDuplication ? 'common.next' : actionTitle,
                onClick: !isDuplication ? () => onClickSave() : () => {},
            },
            stepBackAction: {
                label: 'common.cancel',
            },
            node: firstStep,
        },
        {
            confirmAction: {
                enabled: !isObsoleteHypothesesLoading,
            },
            node: secondStep,
        },
        {
            confirmAction: {
                onClick: onClickSave,
                label: 'common.duplicate',
                enabled: !isDuplicableHypothesesLoading,
            },
            node: thirdStep,
        },
        {
            confirmAction: {
                enabled: !isLoading,
                label: 'common.close',
            },
            stepBackAction: {
                enabled: !!shouldRedirect && !isLoading,
                label: shouldRedirect
                    ? 'humanResources.homeRedirection'
                    : 'common.previous',
                onClick: redirectHome,
            },
            node: fourthStep,
            disableClose: isLoading,
        },
    ].filter((step, index) => filterSteps(index, isDuplication));

    return (
        <MultiStepModal
            isOpen={show}
            onClose={onClose}
            maxWidth="md"
            steps={steps}
        />
    );
}
